// Port of ys_deliverydate/build/delivery/src/tpl/delivery.ts — pure ES module, no DOM deps.
// Reads window.deliveryInfo set by ys_top_banner_delivery.tpl.
// Returns null/'' gracefully when deliveryInfo is missing (dev server, test).

import {getLocale} from '$m7-library/utils/i18n.js';
import {t, plural} from '$utils/lang.js';

// decision: [1]
function getDateString(date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
}

function getDeliveryInfo() {
    return typeof window !== 'undefined' ? window.deliveryInfo ?? null : null;
}

/**
 * Compute delivery date + countdown date for a given shipping type.
 * @param {string} type  deliveryType value from shippingSet (e.g. 'dsv', 'goexpress')
 * @param {number} deltaLocation  extra days offset (basketStore.maxDeliveryDays)
 * @param {Date}   now   current time (injectable for testing)
 * @returns {{ deliveryDate: Date, countdownDate: Date } | null}
 */
export function computeDeliveryDate(type, deltaLocation = 0, now = new Date()) {
    const info = getDeliveryInfo();
    if (!info?.types?.[type]) return null;

    const {
        delta: transitTime,
        deltaIntern: handlingTime,
        switchHour,
        switchMinute,
        saturdayDelivery,
        saturdayPickup,
        saturdayWorkday,
        saturdayWorkdayIntern,
    } = info.types[type];
    const { holidays, m7Days } = info;
    const transitHolidays = info.transitHolidays ?? holidays;

    let dd = new Date(now.getTime());
    const countDown = new Date(now.getTime());
    countDown.setHours(switchHour, switchMinute, 0, 0);

    let handlingDelta = handlingTime + deltaLocation;
    let nonWorkdaysOnly = true;

    const hours = dd.getHours();
    const minutes = dd.getMinutes();
    const day = dd.getDay();
    const dateStr = getDateString(dd);

    if (
        hours > switchHour ||
        (hours === switchHour && minutes >= switchMinute) ||
        (!saturdayPickup && day === 6) ||
        day === 0 ||
        holidays[dateStr] ||
        m7Days[dateStr]
    ) {
        handlingDelta += 1;
        countDown.setDate(countDown.getDate() + 1);
    } else {
        nonWorkdaysOnly = false;
    }

    // Step 1: handling time
    for (let i = 0; i < 28; i++) {
        if (handlingDelta <= 0) break;
        dd.setDate(dd.getDate() + 1);
        const ds = getDateString(dd);
        const d = dd.getDay();

        if (
            d === 0 ||
            holidays[ds] ||
            (!saturdayPickup && handlingDelta === 1 && d === 6) ||
            (!saturdayWorkdayIntern && d === 6) ||
            (handlingDelta > 1 && m7Days[ds])
        ) {
            if (nonWorkdaysOnly) countDown.setDate(countDown.getDate() + 1);
        } else if (handlingDelta === 1 || saturdayPickup || d !== 6) {
            handlingDelta -= 1;
            nonWorkdaysOnly = false;
        }
    }

    // Step 2: transit time
    let transitDelta = transitTime;
    for (let i = 0; i < 14; i++) {
        if (transitDelta <= 0) break;
        dd.setDate(dd.getDate() + 1);
        const ds = getDateString(dd);
        const d = dd.getDay();

        if (
            d === 0 ||
            transitHolidays[ds] ||
            (!saturdayWorkday && d === 6) ||
            (!saturdayDelivery && transitDelta === 1 && d === 6)
        ) {
            // skip non-delivery day
        } else {
            transitDelta -= 1;
            nonWorkdaysOnly = false;
        }
    }

    return { deliveryDate: new Date(dd.getTime()), countdownDate: new Date(countDown.getTime()) };
}

/**
 * Format a delivery date as "morgen" or "Dienstag, 12. Mai".
 * decision: [delivery 2]
 */
export function formatDeliveryDate(date, now = new Date()) {
    const tomorrow = new Date(now.getTime());
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (getDateString(date) === getDateString(tomorrow)) return t('morgen');
    const weekday = date.toLocaleDateString(getLocale(), { weekday: 'long' });
    const dateStr = date.toLocaleString(getLocale(), { day: 'numeric', month: 'long' });
    return `${weekday}, ${dateStr}`;
}

/**
 * Format a countdown distance in ms as "23 Stdn. 41 Min.".
 * decision: [delivery 1]
 */
export function formatDeliveryDistance(distanceMs) {
    if (distanceMs <= 0) return '';
    const hours = Math.floor(distanceMs / (1000 * 60 * 60));
    const minutes = Math.floor((distanceMs % (1000 * 60 * 60)) / (1000 * 60));
    const unit = t(plural(hours, {one: 'Std.', other: 'Stdn.'}));
    return `${hours}\u00A0${unit}\u00A0${minutes}\u00A0${t('Min.')}`;
}
